/**
 * RM-CAN-14: the archive seat's second click on a Room Rail row (the sidebar
 * row's own "Confirm", restored with the confirmThreadArchive setting). One
 * look for the root chip and the nested row; its own module so neither row
 * component imports the other.
 */
export const RAIL_ARCHIVE_CONFIRM_CLASS =
  "inline-flex h-4 shrink-0 cursor-pointer items-center rounded-md bg-destructive/12 px-1.5 text-[10px] font-medium text-destructive hover:bg-destructive/18";

/**
 * RM-CAN-14: the seats a stance ADDS to a row (Local: new child, archive) float over
 * the label's tail while the row is hovered or focused, instead of each reserving its
 * width at rest. At the dock's default width (160px) four reserved seats left a Local
 * row no room for its name at all (witnessed in a fresh profile, 20 September 2026).
 * Floating, nothing the pointer is already on moves, and a World row, which adds
 * none, is drawn exactly as before. `force` keeps it in view for the second click.
 */
export function railAddedSeatsClass(group: "session" | "row", force: boolean): string {
  const base =
    "absolute top-1/2 z-10 -translate-y-1/2 items-center gap-1 rounded-md bg-background/95 px-1 py-0.5 shadow-sm";
  if (force) return `${base} flex`;
  return group === "session"
    ? `${base} hidden group-hover/session:flex group-focus-within/session:flex`
    : `${base} hidden group-hover/row:flex group-focus-within/row:flex`;
}

/** A seat inside the floating cluster: always drawn (the cluster decides when). */
export const RAIL_ADDED_SEAT_CLASS =
  "inline-flex size-3.5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground";

/**
 * The reveal a floating seat wears, in the row's own group: the reserved glyph it
 * replaces stood at 60% while its row was hovered or focused and at full strength under
 * the pointer (FD-CAN-20 law 5; RM-CAN-10 law 3: hover AND focus-within reveal), and a
 * floating seat says the same, so a World row's ⋮ and retire keep their look when they
 * leave the line for the cluster (20 September 2026).
 */
export function railSeatRevealClass(group: "session" | "row"): string {
  return `opacity-60 hover:opacity-100 group-hover/${group}:opacity-60 group-focus-within/${group}:opacity-60`;
}

/** The floating ⋮: in the cluster at every row width (Pin, retire and archive are in it). */
export function railFloatingMenuClass(group: "session" | "row"): string {
  return `${RAIL_ADDED_SEAT_CLASS} ${railSeatRevealClass(group)}`;
}

/** A floating seat that joins the ⋮ from 200px of row width (the pin, retire, new child, archive). */
export function railFloatingSeatClass(group: "session" | "row"): string {
  return `${RAIL_WIDE_ROW_SEAT_CLASS} ${railSeatRevealClass(group)}`;
}

/**
 * A floating row is a size container ("railrow"). Under 200px of row width (the dock's
 * default 160px gives a row about 122px) only the menu seat floats, because four seats are
 * as wide as the row itself and its centre must stay its name; Pin, Retire, New child and
 * Archive are all in that menu. From 200px the pin, the retire, the new-child and the
 * archive seats join it. A World's rows float too since 20 September 2026: their reserved
 * pin, ⋮ and retire seats cost about 110px, which at the default width left a name 12px
 * (measured in the Dev Desk), the defect 323c2b40 had fixed for Local rows only. RM-CAN-10
 * asks that a row may only truncate and that ⋮ and retire are revealed on hover; no
 * register asks for reserved widths.
 */
export const RAIL_ROW_CONTAINER_CLASS = "@container/railrow";
export const RAIL_WIDE_ROW_SEAT_CLASS =
  "hidden @min-[200px]/railrow:inline-flex size-3.5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground";
export const RAIL_WIDE_ROW_ONLY_CLASS = "hidden @min-[200px]/railrow:contents";
